// src/history/ToolHistoryPage.js
import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {
    Box, Button, Typography, Tooltip, Fab, Stack, IconButton,
    Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {useParams, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {alpha, useTheme} from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LanguageThemeBar from '../LanguageThemeBar';
import HistoryTable from './HistoryTable';
import ViewerContainer from '../PdfViewer/ViewerContainer/ViewerContainer';
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';
import * as XLSX from 'xlsx-js-style';
import VerticalResizeHandle from "../VerticalResizeHandle";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

// === BBox offset to apply before saving to the backend ===
// Adjust these values to your desired offset (in the same coordinate space as your backend expects).
const BBOX_OFFSET = {dx: 8, dy: 8};
const offsetBbox = (bbox, dx = BBOX_OFFSET.dx, dy = BBOX_OFFSET.dy) => {
    if (!Array.isArray(bbox) || bbox.length !== 4) return bbox;
    return [bbox[0] + dx, bbox[1] + dy, bbox[2] + dx, bbox[3] + dy];
};
const reverseOffsetBbox = (bbox) => offsetBbox(bbox, -BBOX_OFFSET.dx, -BBOX_OFFSET.dy);
const DRAW_BBOX_OFFSET = {dx: 10, dy: 10};
const displayBboxToPersistedBbox = (bbox) =>
    offsetBbox(bbox, BBOX_OFFSET.dx - DRAW_BBOX_OFFSET.dx, BBOX_OFFSET.dy - DRAW_BBOX_OFFSET.dy);

export default function ToolHistoryPage() {
    const {toolId} = useParams();
    const {state} = useLocation();
    const toolName = state?.toolName || `Tool ${toolId}`;
    const theme = useTheme();
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [toolDetails, setToolDetails] = useState(null);
    const [page, setPage] = useState(1);
    const [paged, setPaged] = useState({items: [], total: 0, has_next: false, has_prev: false});
    const [loading, setLoading] = useState(false);

    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [editedValues, setEditedValues] = useState({}); // fileName -> values array

    const [selectedFile, setSelectedFile] = useState(null);         // <<< File object
    const [selectedExecutionId, setSelectedExecutionId] = useState(null);
    const [selectedAnnotations, setSelectedAnnotations] = useState([]);

    const [pageSize, setPageSize] = useState(10);

    // 🔔 jiggle control (same as Results page)
    const [peekKeyExternal, setPeekKeyExternal] = useState(null);
    const [peekNonce, setPeekNonce] = useState(0);
    const [hoveredKeyExternal, setHoveredKeyExternal] = useState(null);
    const makeEntityKey = (page, bbox) => `${page}_entity_${bbox.join('_')}`;
    const triggerPeek = (page, bbox) => {
        setPeekKeyExternal(makeEntityKey(page, bbox));
        setPeekNonce(n => n + 1);
    };

    const FORCE_DRAW = "__force_draw__";

    const [selectedIds, setSelectedIds] = useState(new Set());

    const toggleRowSelected = useCallback((executionId, on) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (on) next.add(executionId); else next.delete(executionId);
            return next;
        });
    }, []);

    const clearSelection = () => setSelectedIds(new Set());

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Open the Material UI dialog from the bulk action; actual deletion happens in handleConfirmDelete
    const handleConfirmDelete = async () => {
        if (selectedIds.size === 0) {
            setDeleteOpen(false);
            return;
        }
        setDeleting(true);
        try {
            const resp = await apiFetch(`${API_BASE}/delete_executions`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ids: Array.from(selectedIds)}),
            });
            if (!resp.ok) {
                let msg = resp.statusText || resp.status;
                try {
                    const err = await resp.json();
                    msg = err.error || msg;
                } catch {
                }
                alert(`${t('delete_failed') || 'Delete failed'}: ${msg}`);
                return;
            }
            clearSelection();
            setDeleteOpen(false);
            // Reload current page (handles empty page -> go to previous if you want)
            loadPage(page);
        } catch (e) {
            console.error(e);
            alert(t('delete_failed') || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    // Compact bulk actions (shown only when rows are selected)
    const bulkActions = selectedIds.size > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption">
                {selectedIds.size} {t('pagination.rows') || 'rows'} {t('selected') || 'selected'}
            </Typography>
            <Tooltip title={t('deleteSelected') || 'Delete selected'}>
                <IconButton
                    color="error"
                    size="small"
                    onClick={() => setDeleteOpen(true)}
                > <DeleteOutlineRoundedIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Stack>
    ) : null;

    // Group support (same as results page)
    const makeGroupIdFromAnnotation = (a) => {
        if (!a) return null;
        const pageNum = (a.page ?? a.source?.page ?? 1);
        const gb = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
        const suffix = Array.isArray(gb) ? gb.join('_') : 'nogb';
        return a.group_id || `${pageNum}_group_${a.group_label || 'group'}_${suffix}`;
    };
    const [redrawGroupId, setRedrawGroupId] = useState(null);
    // 🔧 Redraw workflow (same as Results page)
    const [allowEdits, setAllowEdits] = useState(false);
    const [removeKeyExternal, setRemoveKeyExternal] = useState(null);
    const [removeNonce, setRemoveNonce] = useState(0);
    const [focusPageExternal, setFocusPageExternal] = useState(null);
    const [overrides, setOverrides] = useState({}); // overrides[execId][colIndex] = {page,bbox}
    const [activeRedraw, setActiveRedraw] = useState(null); // { executionId, colIndex, annotationIds, removeKeys }
    const [extractingCell, setExtractingCell] = useState(null);

    // Resizable viewer width (% of window width)
    const [viewerWidthPct, setViewerWidthPct] = useState(50);
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    // Track which cell is being processed during drag-and-drop
    const [processingCell, setProcessingCell] = useState(null); // { executionId, colIndex }
    const [viewerKey, setViewerKey] = useState(0);

    // --- helpers to normalize and key-match ---
    const norm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[：:]+$/g, "");
    const keyFromAnnotation = (a) => {
        if (!a || !a.type) return null;
        if (a.type === "key_value") {
            const ft = a.field_text;
            if (typeof ft === "string" && ft.trim()) return norm(ft);
            const f = a.field;
            if (typeof f === "string" && f.trim()) return norm(f);
            if (f && typeof f === "object") {
                const t = f.text || f.label || "";
                if (t && t.trim()) return norm(t);
            }
            return null;
        }
        if (a.type === "entity" || a.type === "summary_row") {
            const et = a.entity_type || a.label;
            if (typeof et === "string" && et.trim()) return norm(et);
            return null;
        }
        return null;
    };
    // History has 3 fixed leading columns: Extracted | Updated | Filename
    const getLeafLabels = (headers) => {
        if (!headers?.length) return [];
        const rows = headers.length, cols = headers[0].length;
        const labels = [];
        for (let c = 3; c < cols; c++) { // skip 3 fixed
            let label = "";
            for (let r = rows - 1; r >= 0; r--) {
                const v = headers[r][c];
                if (v && String(v).trim()) {
                    label = String(v);
                    break;
                }
            }
            if (!label) {
                for (let r = 0; r < rows; r++) {
                    const v = headers[r][c];
                    if (v && String(v).trim()) {
                        label = String(v);
                        break;
                    }
                }
            }
            labels.push(label);
        }
        return labels;
    };

    // Build all remove-keys for a given data column (e.g., both field+value for key_value)
    const computeRemoveKeysForCell = (executionId, colIndex) => {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return [];
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        // mirror HistoryTable: prepend 3 fixed cells to the FIRST header row
        headers.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift("Filename");
                row.unshift("Updated");
                row.unshift("Extracted");
            } else {
                row.unshift("");
                row.unshift("");
                row.unshift("");
            }
        });
        const leaf = getLeafLabels(headers);
        const label = leaf[colIndex];
        const targetKey = norm(label);
        const keys = new Set();
        const add = (page, bbox) => {
            if (!Array.isArray(bbox) || bbox.length !== 4) return;
            const pageNum = page ?? 1;
            keys.add(makeEntityKey(pageNum, bbox));
            keys.add(makeEntityKey(pageNum, reverseOffsetBbox(bbox)));
        };
        for (const a of (res.data.annotations || [])) {
            const k = keyFromAnnotation(a);
            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                const page = a.page ?? a.source?.page ?? 1;
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                        const vb = item?.value?.bbox;
                        if (Array.isArray(vb) && vb.length === 4) add(page, vb);
                    }
                }
                continue;
            }
            if (k && (k === targetKey || k.includes(targetKey))) {
                const page = a.value?.page ?? a.field?.page ?? a.page ?? 1;
                if (a.type === "key_value") {
                    if (a.field?.bbox) add(page, a.field.bbox);
                    if (a.value?.bbox) add(page, a.value.bbox);

                } else if (a.bbox) {
                    add(page, a.bbox);
                }
            }
        }
        return Array.from(keys);
    };

    const computeAnnotationIdsForCell = (executionId, colIndex) => {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return [];
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift("Filename");
                row.unshift("Updated");
                row.unshift("Extracted");
            } else {
                row.unshift("");
                row.unshift("");
                row.unshift("");
            }
        });
        const leaf = getLeafLabels(headers);
        const targetKey = norm(leaf[colIndex]);
        const ids = new Set();

        for (const a of (res.data.annotations || [])) {
            const k = keyFromAnnotation(a);
            if (k && (k === targetKey || k.includes(targetKey)) && a.id) {
                ids.add(a.id);
                continue;
            }
            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey)) && a.id) {
                        ids.add(a.id);
                        break;
                    }
                }
            }
        }
        return Array.from(ids);
    };

    // Function to load tool details
    const loadToolDetails = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE}/get_tool_details/${toolId}`);
            if (res.ok) {
                const data = await res.json();
                console.log('[loadToolDetails] Fetched tool details:', data);
                console.log('[loadToolDetails] headerConfig from backend:', JSON.stringify(data.headerConfig));
                setToolDetails(data);
                return data;
            } else if (res.status === 401) {
                // Not authenticated even after refresh → back to login
                navigate('/');
            }
        } catch (e) {
            console.error('Error fetching tool details', e);
        }
    }, [toolId, navigate]);

    useEffect(() => {
        loadToolDetails();
    }, [loadToolDetails]);

    const loadPage = useCallback(async (p) => {
        setLoading(true);
        try {
            const url = `${API_BASE}/list_executions?tool_id=${toolId}&page=${p}&page_size=${pageSize}`;
            const res = await apiFetch(url);

            if (!res.ok) {
                // Try to parse error message if provided
                let msg = 'Failed to load history';
                try {
                    const errData = await res.json();
                    msg = errData.error || msg;
                } catch {
                    // response body not JSON
                }
                throw new Error(msg);
            }

            const data = await res.json();
            setPaged(data);
            setPage(p);
        } catch (e) {
            console.error(e);
            alert('Failed to load history.');
            // If refresh failed and we still got a 401, redirect to login
            if (e.message.includes('401')) navigate('/');
        } finally {
            setLoading(false);
        }
    }, [toolId, pageSize, navigate]);

    useEffect(() => {
        loadPage(1);
    }, [loadPage]);

    // Adapt executions to the same "results" shape used by editing code
    const results = useMemo(() => {
        return (paged.items || []).map(ex => ({
            fileName: ex.file_name,
            status: 'success',
            data: {
                execution_id: ex.id,
                annotations: ex.result_json
            },
            created_at: ex.created_at,
            updated_at: ex.updated_at
        }));
    }, [paged]);

    const flattenForViewer = (annotations) => {
        const flat = [];
        (annotations || []).forEach(a => {
            const page = (a.page ?? 1);
            const push = (bbox, extra = {}) => {
                if (Array.isArray(bbox) && bbox.length === 4) {
                    const __uid = `${page}_entity_${bbox.join('_')}`; // normalize to 'entity'
                    flat.push({page, bbox, type: 'entity', __uid, annotationId: a.id, ...extra});
                }
            };
            if (a.type === 'key_value') {
                if (a.field?.bbox) push(a.field.bbox, {label: a.field?.text ?? ''});
                if (a.value?.bbox) push(a.value.bbox, {label: a.value?.text ?? ''});
            } else if (a.type === 'key_value_group' && Array.isArray(a.items)) {
                const pageNum2 = a.page ?? a.source?.page ?? page;
                const groupBox = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
                const groupId = makeGroupIdFromAnnotation(a);
                for (const item of a.items) {
                    const vb = item?.value?.bbox;
                    if (Array.isArray(vb) && vb.length === 4) {
                        const uid2 = `${pageNum2}_entity_${vb.join('_')}`;
                        push(vb, {label: item?.field?.text ?? '', groupId, page: pageNum2, __uid: uid2});
                    }
                }
            } else if (a.type === 'entity' || a.type === 'summary_row') {
                if (a.bbox) push(a.bbox, {label: a.label ?? a.entity_type ?? ''});
            } else if (a.type === 'table_row' && Array.isArray(a.columns)) {
                a.columns.forEach((col, i) => col?.bbox && push(col.bbox, {colIndex: i, label: col.text ?? ''}));
            }
            // Removed catch-all else clause that was creating unwanted extra boxes
        });
        return flat;
    };

    // 🔑 IMPORTANT: Build a **File** from the blob and pass that to ViewerContainer
    const openFile = async (executionId, annotations, fileName) => {
        try {
            setAllowEdits(false);
            setActiveRedraw(null);
            setRemoveKeyExternal(null);
            setFocusPageExternal(null);
            setRedrawGroupId(null);

            const res = await apiFetch(
                `${API_BASE}/execution_file/${executionId}?t=${Date.now()}`,
                {cache: 'no-store'}
            );

            if (!res.ok) {
                let msg = 'Download failed';
                try {
                    const err = await res.json();
                    msg = err.error || msg;
                } catch {
                    // body not JSON
                }
                throw new Error(msg);
            }

            const blob = await res.blob();
            const file = new File([blob], fileName || 'document.pdf', {
                type: 'application/pdf',
            });
            setSelectedFile(file);
            setSelectedAnnotations(flattenForViewer(annotations));
            setSelectedExecutionId(executionId);
            return true; // allow awaiting
        } catch (e) {
            console.error(e);
            alert('Could not open the file.');
            return false;
        }
    };

    const closeViewer = useCallback(() => {
        setSelectedAnnotations([]);
        setSelectedFile(null);
        setSelectedExecutionId(null);
        setAllowEdits(false);
        setActiveRedraw(null);
        setRemoveKeyExternal(null);
        setFocusPageExternal(null);
        setRedrawGroupId(null);
    }, []);

    // Handle header editing
    const handleHeaderEdit = async (rowIndex, cellIndex, newValue, colSpan = 1) => {
        if (!toolDetails?.headerConfig) return;

        console.log('handleHeaderEdit called:', {rowIndex, cellIndex, newValue, colSpan});

        // Clone the headerConfig
        const updatedConfig = JSON.parse(JSON.stringify(toolDetails.headerConfig));
        console.log('Original headerConfig:', updatedConfig);

        // Map the cellIndex back to the original headerConfig
        // HistoryTable adds 4 fixed columns (checkbox, extracted, updated, filename) at the beginning
        // So we need to subtract 4 to get the actual column index in headerConfig
        const actualColIndex = cellIndex - 4;
        console.log('Mapped to actualColIndex:', actualColIndex);

        // Make sure we're not trying to edit a fixed column or out of bounds
        if (actualColIndex < 0 || actualColIndex >= updatedConfig[0]?.length) {
            console.warn('Invalid column index for header edit');
            return;
        }

        // Update only the cells covered by the colSpan of the clicked merged cell
        // colSpan tells us how many consecutive cells are merged together
        const mergeStart = actualColIndex;
        const mergeEnd = actualColIndex + colSpan - 1;

        console.log('Merge range from colSpan:', {mergeStart, mergeEnd, colSpan});
        console.log('Updating row', rowIndex, 'columns', mergeStart, 'to', mergeEnd, 'with value:', newValue);

        // Check if the new value would cause unwanted merging with adjacent cells
        // (only check if we're updating a single cell, not an already-merged range)
        if (colSpan === 1) {
            const leftValue = actualColIndex > 0 ? updatedConfig[rowIndex][actualColIndex - 1] : null;
            const rightValue = actualColIndex < updatedConfig[rowIndex].length - 1 ? updatedConfig[rowIndex][actualColIndex + 1] : null;

            if (newValue && (newValue === leftValue || newValue === rightValue)) {
                const confirmMerge = window.confirm(
                    'The new value matches an adjacent column header, which will cause them to merge. Do you want to continue?'
                );
                if (!confirmMerge) {
                    return;
                }
            }
        }

        // Update the range of cells that belong to this merged cell
        for (let i = mergeStart; i <= mergeEnd; i++) {
            if (i < updatedConfig[rowIndex].length) {
                console.log('Setting updatedConfig[' + rowIndex + '][' + i + '] =', newValue);
                updatedConfig[rowIndex][i] = newValue;
                console.log('After setting, value is:', updatedConfig[rowIndex][i]);
            }
        }

        console.log('Updated headerConfig:', updatedConfig);
        console.log('Updated headerConfig row 0:', updatedConfig[0]);

        try {
            // Update the backend
            const requestBody = {
                name: toolDetails.name,
                description: toolDetails.description,
                headerConfig: updatedConfig
            };
            console.log('[handleHeaderEdit] Sending to backend:', JSON.stringify(requestBody));

            const response = await apiFetch(`${API_BASE}/update_tool/${toolId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Backend error:', errorData);
                throw new Error(errorData.error || 'Failed to update tool');
            }

            const responseData = await response.json();
            console.log('Backend update successful, response:', responseData);

            // Reload tool details from the backend to ensure everything is in sync
            await loadToolDetails();
        } catch (error) {
            console.error('Error updating header:', error);
            alert('Failed to update header. Please try again.');
        }
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeViewer();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [closeViewer]);

    // Save edits back to the execution (same logic as Results page)
    const handleConfirm = async () => {
        if (!editingCell) return;
        const {executionId, fileName, index} = editingCell;
        const res = results.find(r => r?.data?.execution_id === executionId)
            || results.find(r => r.fileName === fileName);
        if (!res) return;


        const cloned = JSON.parse(JSON.stringify(res.data.annotations || []));

        // --- Build the same headers shape HistoryTable uses (prepend the 3 fixed cells) ---
        const headers = JSON.parse(JSON.stringify(toolDetails?.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift("Filename");
                row.unshift("Updated");
                row.unshift("Extracted");
            } else {
                row.unshift("");
                row.unshift("");
                row.unshift("");
            }
        });

        // Helpers copied from the table’s logic
        const norm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[：:]+$/g, "");
        const getLeafLabels = (h) => {
            if (!h?.length) return [];
            const rows = h.length, cols = h[0].length, labels = [];
            for (let c = 3; c < cols; c++) {
                let label = "";
                for (let r = rows - 1; r >= 0; r--) {
                    const v = h[r][c];
                    if (v && String(v).trim()) {
                        label = String(v);
                        break;
                    }
                }
                if (!label) {
                    for (let r = 0; r < rows; r++) {
                        const v = h[r][c];
                        if (v && String(v).trim()) {
                            label = String(v);
                            break;
                        }
                    }
                }
                labels.push(label);
            }
            return labels;
        };
        const keyFromAnnotation = (a) => {
            if (!a || !a.type) return null;
            if (a.type === "key_value") {
                const ft = a.field_text;
                if (typeof ft === "string" && ft.trim()) return norm(ft);
                const f = a.field;
                if (typeof f === "string" && f.trim()) return norm(f);
                if (f && typeof f === "object") {
                    const t = f.text || f.label || "";
                    if (t && t.trim()) return norm(t);
                }
                return null;
            }
            if (a.type === "entity" || a.type === "summary_row") {
                const et = a.entity_type || a.label;
                if (typeof et === "string" && et.trim()) return norm(et);
                return null;
            }
            return null;
        };

        const leaf = getLeafLabels(headers);
        const targetKey = norm(leaf[index] ?? "");
        const newValue = editValue ?? "";

        let updated = false;
        for (const a of cloned) {
            const k = keyFromAnnotation(a);
            if (k && (k === targetKey || k.includes(targetKey))) {
                if (a.type === "key_value") {
                    if (!a.value) a.value = {};
                    a.value.text = newValue;
                } else if (a.type === "entity" || a.type === "summary_row") {
                    a.text = newValue;
                }
                updated = true;
                break;
            }

            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                        if (!item.value) item.value = {};
                        item.value.text = newValue;
                        updated = true;
                        break;
                    }
                }
                if (updated) break;
            }
        }

        if (!updated) {
            const fallback = {
                type: "entity",
                entity_type: leaf[index] ?? "",
                label: leaf[index] ?? "",
                text: newValue,
            };
            const existingOverride = overrides?.[executionId]?.[index];
            if (existingOverride?.page) fallback.page = existingOverride.page;
            if (existingOverride?.bbox) fallback.bbox = existingOverride.bbox;
            cloned.push(fallback);
        }


        try {

            const resp = await apiFetch(`${API_BASE}/update_execution/${res.data.execution_id}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({result_json: cloned}),
            });

            if (!resp.ok) {
                let msg = resp.status;
                try {
                    const err = await resp.json();
                    msg = err.error || msg;
                } catch {
                    // response body not JSON
                }
                alert(`Failed to save: ${msg}`);
            } else {
                if (res?.data) {
                    res.data.annotations = cloned;
                }
                // mirror the UI immediately
                setEditedValues((prev) => {
                    const key = executionId ?? fileName;
                    const current = prev[key] || [];
                    const next = [...current];
                    next[index] = newValue;
                    return {...prev, [key]: next};
                });
                // reload page to refresh "updated_at" sort if needed
                loadPage(page);
            }
        } catch (e) {
            console.error(e);
            alert('Error updating execution.');
        } finally {
            setEditingCell(null);
            setEditValue("");
        }
    };

    // 💾 Persist bbox override (single value box) like Results page
    async function applyOverrideAndPersist({executionId, colIndex, page, bbox, text = null, annotationIds = []}) {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return;
        // Save bbox as-is, without offset
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift("Filename");
                row.unshift("Updated");
                row.unshift("Extracted");
            } else {
                row.unshift("");
                row.unshift("");
                row.unshift("");
            }
        });
        const leaf = getLeafLabels(headers);
        const targetKey = norm(leaf[colIndex]);
        const cloned = JSON.parse(JSON.stringify(res.data.annotations || []));

        // Ensure all annotations have stable IDs
        cloned.forEach((a, idx) => {
            if (!a.id) {
                a.id = `ann_${a.type}_${idx}_${Date.now()}`;
            }
        });

        let updated = false;
        let matchedAnnotationId = null;
        console.log('[applyOverrideAndPersist] Looking for annotation with targetKey:', targetKey);
        const targetAnnotationIds = new Set(annotationIds.filter(Boolean));
        for (const a of cloned) {
            const k = keyFromAnnotation(a);
            console.log('[applyOverrideAndPersist] Checking annotation:', {type: a.type, key: k, id: a.id});
            const matchesById = a.id && targetAnnotationIds.has(a.id);
            const matchesByKey = k && (k === targetKey || k.includes(targetKey));
            const matchesGroupItem = a?.type === "key_value_group" && Array.isArray(a.items) && a.items.some((item) => {
                const itemKey = norm(
                    (item?.field?.text) ??
                    (typeof item?.field === "string" ? item.field : "")
                );
                return itemKey && (itemKey === targetKey || itemKey.includes(targetKey));
            });

            if (matchesById || matchesByKey || matchesGroupItem) {
                console.log('[applyOverrideAndPersist] MATCH FOUND! Updating annotation:', a.id);
                matchedAnnotationId = a.id;
                if (a.type === "key_value") {
                    if (!a.value) a.value = {};
                    a.value.page = page ?? a.value.page ?? a.page ?? 1;
                    a.value.bbox = bbox;
                    if (text !== null) a.value.text = text;
                    if (a.field) delete a.field.bbox; // drop key box
                } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                    const pageNum = page ?? a.page ?? a.source?.page ?? 1;
                    let matched = false;
                    for (const item of a.items) {
                        const itemKey = norm(
                            (item?.field?.text) ??
                            (typeof item?.field === "string" ? item.field : "")
                        );
                        if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                            if (!item.value) item.value = {};
                            item.value.page = pageNum;
                            item.value.bbox = bbox;
                            if (text !== null) item.value.text = text;
                            matched = true;
                            break;
                        }
                    }
                    if (!matched && a.items.length) {
                        const val = {page: pageNum, bbox};
                        if (text !== null) val.text = text;
                        a.items[0].value = val;
                    }
                } else if (a.type === "entity" || a.type === "summary_row") {
                    a.page = page ?? a.page ?? 1;
                    a.bbox = bbox;
                    if (text !== null) a.text = text;
                    // Preserve the entity_type so we can match columns after drag-and-drop
                    if (!a.entity_type) {
                        a.entity_type = targetKey;
                    }
                }
                updated = true;
                break;
            }
        }
        if (!updated) {
            console.log('[applyOverrideAndPersist] No match found, creating new annotation');
            const label = leaf[colIndex] ?? "";
            const newId = `ann_entity_${cloned.length}_${Date.now()}`;
            cloned.push({
                id: newId,
                type: "entity",
                label,
                entity_type: targetKey, // Store the column header for matching after drag-and-drop
                text: text ?? "",
                page: page ?? 1,
                bbox: bbox
            });
            matchedAnnotationId = newId;
            updated = true;
        }
        try {
            const resp = await apiFetch(`${API_BASE}/update_execution/${res.data.execution_id}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({result_json: cloned}),
            });

            if (!resp.ok) {
                let msg = resp.statusText || resp.status;
                try {
                    const err = await resp.json();
                    msg = err.error || msg;
                } catch {
                    // response body not JSON, leave msg as status/statusText
                }
                console.error('Failed to save new bbox:', msg);
                alert(`Failed to save annotation change: ${msg}`);
                return matchedAnnotationId; // Still return the ID even if save fails
            }
            // keep local page data in sync
            res.data.annotations = cloned;
        } catch (e) {
            console.error("Network error while saving new bbox:", e);
            alert("Error saving annotation change.");
            console.log('[applyOverrideAndPersist] Network error, but returning annotation ID anyway:', matchedAnnotationId);
            return matchedAnnotationId;
        }

        // Return the annotation ID that was created/updated
        console.log('[applyOverrideAndPersist] Returning annotation ID:', matchedAnnotationId);
        return matchedAnnotationId;
    }

    // 🔧 When the viewer reports the freshly drawn rectangle
    const handleAnnotationAdded = async (newAnn) => {
        if (!activeRedraw) return;
        const {executionId, colIndex, annotationIds = [], removeKeys = []} = activeRedraw;
        const {page, bbox, pageImage, bboxNorm, __raw_bbox} = newAnn || {};

        const bboxRaw = (Array.isArray(__raw_bbox) && __raw_bbox.length === 4)
            ? __raw_bbox
            : bbox;
        // Apply the offset before saving to database
        const bboxForBackend = offsetBbox(bboxRaw);
        try {
            if (page && Array.isArray(bbox) && bbox.length === 4) {
                // Try to tag override with the right groupId so the viewer draws the dashed outline
                let overrideGroupId = null;
                try {
                    const resLocal = results.find(r => r?.data?.execution_id === executionId);
                    const annsLocal = resLocal?.data?.annotations || [];
                    if (toolDetails?.headerConfig) {
                        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
                        headers.forEach((row, idx) => {
                            if (idx === 0) {
                                row.unshift("Filename");
                                row.unshift("Updated");
                                row.unshift("Extracted");
                            } else {
                                row.unshift("");
                                row.unshift("");
                                row.unshift("");
                            }
                        });
                        const leaf = getLeafLabels(headers);
                        const targetKey = norm(leaf[colIndex]);
                        for (const a of annsLocal) {
                            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                                for (const it of a.items) {
                                    const itKey = norm((it?.field?.text) ?? (typeof it?.field === "string" ? it.field : ""));
                                    if (itKey && (itKey === targetKey || itKey.includes(targetKey))) {
                                        overrideGroupId = makeGroupIdFromAnnotation(a);
                                        break;
                                    }
                                }
                                if (overrideGroupId) break;
                            }
                        }
                    }
                } catch {
                }
                if (!overrideGroupId && redrawGroupId) overrideGroupId = redrawGroupId;

                // 💾 persist to backend and get the annotation ID
                const dbAnnotationId = await applyOverrideAndPersist({
                    executionId,
                    colIndex,
                    page,
                    bbox: bboxForBackend,
                    annotationIds,
                }) || `new_${executionId}_${colIndex}`;
                console.log('[handleAnnotationAdded] Got annotation ID from persist:', dbAnnotationId);
                console.log('[handleAnnotationAdded] dbAnnotationId type:', typeof dbAnnotationId);
                console.log('[handleAnnotationAdded] dbAnnotationId value:', JSON.stringify(dbAnnotationId));

                setOverrides(prev => ({
                    ...prev,
                    [executionId]: {
                        ...(prev[executionId] || {}),
                        [colIndex]: {page, bbox, ...(overrideGroupId ? {groupId: overrideGroupId} : {})}
                    }
                }));

                const newBoxUid = makeEntityKey(page, bbox);
                console.log('[handleAnnotationAdded] Setting annotation with ID:', dbAnnotationId);
                setSelectedAnnotations(prev => {
                    const removeKeySet = new Set(removeKeys.filter(k => k && k !== FORCE_DRAW));
                    const targetAnnotationIds = new Set([
                        ...annotationIds.filter(Boolean),
                        dbAnnotationId,
                    ]);
                    const withoutOldBoxes = prev.filter((ann) => {
                        if (ann.__uid && removeKeySet.has(ann.__uid) && ann.__uid !== newBoxUid) return false;
                        if (!ann.groupId && ann.annotationId && targetAnnotationIds.has(ann.annotationId) && ann.__uid !== newBoxUid) return false;
                        return true;
                    });

                    // Check if this box already exists
                    const existingIndex = withoutOldBoxes.findIndex(ann => ann.__uid === newBoxUid);

                    if (existingIndex >= 0) {
                        // Update existing box
                        const updated = withoutOldBoxes.map((ann, idx) =>
                            idx === existingIndex
                                ? {...ann, bbox, page, annotationId: dbAnnotationId, ...(overrideGroupId ? {groupId: overrideGroupId} : {})}
                                : ann
                        );
                        console.log('[handleAnnotationAdded] Updated existing annotation:', updated[existingIndex]);
                        return updated;
                    } else {
                        // Add new box
                        const newAnn = {
                            page,
                            bbox,
                            type: 'entity',
                            __uid: newBoxUid,
                            annotationId: dbAnnotationId,
                            ...(overrideGroupId ? {groupId: overrideGroupId} : {})
                        };
                        console.log('[handleAnnotationAdded] Adding new annotation:', newAnn);
                        return [...withoutOldBoxes, newAnn];
                    }
                });

                setTimeout(() => {
                    setPeekKeyExternal(makeEntityKey(page, bbox));
                    setPeekNonce(n => n + 1);
                }, 100);

                // if we have the page canvas + normalized bbox, ask backend for text and auto-fill
                if (pageImage && Array.isArray(bboxNorm) && bboxNorm.length === 4) {
                    setExtractingCell({executionId, colIndex});
                    try {
                        const resp = await apiFetch(`${API_BASE}/extract-snippet-text`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({
                                page_image: pageImage,
                                bbox_norm: bboxNorm,
                                // provider: 'openai',   // adjust if you expose provider/model
                                // model: 'gpt-4.1',
                            }),
                        });

                        const data = await resp.json();
                        const newText = (data && data.text) || "";

                        if (typeof newText === "string") {
                            const res = results.find(r => r?.data?.execution_id === executionId);
                            if (res?.data?.annotations && toolDetails?.headerConfig) {
                                // Build the same headers that HistoryTable renders (prepend 3 fixed cols)
                                const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
                                headers.forEach((row, idx) => {
                                    if (idx === 0) {
                                        row.unshift("Filename");
                                        row.unshift("Updated");
                                        row.unshift("Extracted");
                                    } else {
                                        row.unshift("");
                                        row.unshift("");
                                        row.unshift("");
                                    }
                                });
                                const leaf = getLeafLabels(headers);
                                const targetKey = norm(leaf[colIndex]);

                                const cloned = JSON.parse(JSON.stringify(res.data.annotations || []));
                                let updated = false;
                                for (const a of cloned) {
                                    const k = keyFromAnnotation(a);
                                    if (!k) continue;
                                    if (k === targetKey || k.includes(targetKey)) {
                                        if (a.type === "key_value") {
                                            if (!a.value) a.value = {};
                                            a.value.text = newText;
                                        } else if (a.type === "entity" || a.type === "summary_row") {
                                            a.text = newText;
                                        }
                                        updated = true;
                                        break;
                                    }
                                }
                                if (!updated) {
                                    const label = leaf[colIndex] ?? "";
                                    cloned.push({type: "entity", label, text: newText, page: page ?? 1, bbox});
                                }
                                // 3) save updated annotations back to this execution

                                const saveResp = await apiFetch(`${API_BASE}/update_execution/${executionId}`, {
                                    method: 'PUT',
                                    headers: {'Content-Type': 'application/json'},
                                    body: JSON.stringify({result_json: cloned}),
                                });

                                if (saveResp.ok) {
                                    res.data.annotations = cloned; // keep local in sync
                                    setEditedValues(prev => {
                                        const base = prev[executionId] ?? Array(leaf.length).fill(undefined);
                                        const next = [...base];
                                        next[colIndex] = newText;
                                        return {...prev, [executionId]: next};
                                    });
                                    // refresh list so "Updated" timestamp reflects the change
                                    loadPage(page);
                                } else {
                                    let msg = saveResp.statusText || saveResp.status;
                                    try {
                                        const err = await saveResp.json();
                                        msg = err.error || msg;
                                    } catch {
                                        // non-JSON error response
                                    }
                                    console.error('Failed to save text:', msg);
                                }
                            }
                        }

                    } catch
                        (e) {
                        console.warn("extract-snippet-text failed", e);
                    }
                }
            }
        } finally {
            setExtractingCell(null);
            setAllowEdits(false);
            setActiveRedraw(null);
            setRemoveKeyExternal(null);
            setFocusPageExternal(null);
            setRedrawGroupId(null);
        }
    };

    // Handler for when an annotation is moved via drag-and-drop
    const handleAnnotationMoved = async (movedData) => {
        const {annotation, bbox, page, oldAnnotation, key, pageImage, bboxNorm} = movedData || {};

        console.log('[handleAnnotationMoved] Received:', {annotation, bbox, page, key, hasPageImage: !!pageImage, hasBboxNorm: !!bboxNorm});

        if (!annotation || !bbox || !page) return;
        if (!selectedExecutionId) return; // Must have an active execution

        // Prevent concurrent processing - if already processing, ignore this call
        if (processingCell) {
            console.log('[handleAnnotationMoved] Already processing, ignoring duplicate call');
            return;
        }

        const executionId = selectedExecutionId;

        console.log('[handleAnnotationMoved] Processing with context:', {
            executionId,
            selectedFileName: selectedFile?.name,
            annotationPage: page,
            toolId
        });

        // Find which column this annotation represents by matching label/entity_type from the DATABASE
        // Don't rely on selectedAnnotations state - go directly to the source of truth
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations) {
            console.log('[handleAnnotationMoved] No annotations found in results');
            return;
        }

        const headers = JSON.parse(JSON.stringify(toolDetails?.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift("Filename");
                row.unshift("Updated");
                row.unshift("Extracted");
            } else {
                row.unshift("");
                row.unshift("");
                row.unshift("");
            }
        });
        const leafHeaders = getLeafLabels(headers);

        const movedAnnotationId = annotation?.annotationId || annotation?.id || oldAnnotation?.annotationId || oldAnnotation?.id;
        let bestMatch = movedAnnotationId
            ? res.data.annotations.find(ann => ann?.id === movedAnnotationId)
            : null;
        let minDistance = Infinity;

        // Fallback: find the annotation whose bbox is closest to oldAnnotation.bbox.
        for (const ann of res.data.annotations) {
            if (bestMatch) break;
            // Get the annotation's current bbox (accounting for different types)
            let annBbox = null;
            let annPage = ann.page || 1;

            if (ann.type === 'key_value' && ann.value?.bbox) {
                annBbox = ann.value.bbox;
                annPage = ann.value.page || ann.page || 1;
            } else if (ann.type === 'entity' || ann.type === 'summary_row') {
                annBbox = ann.bbox;
            }

            if (!annBbox || annPage !== page) continue;

            // Apply reverse offset to match display coordinates
            const displayBbox = reverseOffsetBbox(annBbox);

            // Calculate distance to oldAnnotation.bbox if available, otherwise use key's bbox
            const referenceBbox = oldAnnotation?.bbox || (key ? key.split('_').slice(2).map(Number) : null);
            if (!referenceBbox || referenceBbox.length !== 4) continue;

            const distance = Math.sqrt(
                Math.pow(displayBbox[0] - referenceBbox[0], 2) +
                Math.pow(displayBbox[1] - referenceBbox[1], 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                bestMatch = ann;
            }
        }

        if (!bestMatch) {
            console.log('[handleAnnotationMoved] Could not find matching annotation in database');
            return;
        }

        console.log('[handleAnnotationMoved] Found best match annotation:', bestMatch.id, 'distance:', minDistance);

        // Now find which column this annotation belongs to
        const annKey = keyFromAnnotation(bestMatch) || norm(annotation?.entity_type ?? annotation?.label ?? "");
        if (!annKey) {
            console.log('[handleAnnotationMoved] No key found for annotation');
            return;
        }

        let colIndex = -1;
        for (let c = 0; c < leafHeaders.length; c++) {
            if (norm(leafHeaders[c]) === annKey || norm(leafHeaders[c]).includes(annKey)) {
                colIndex = c;
                break;
            }
        }

        console.log('[handleAnnotationMoved] Found colIndex:', colIndex, 'for key:', annKey);

        if (colIndex >= 0) {
            try {
                // Set processing state for this cell
                setProcessingCell({executionId, colIndex});

                let extractedText = '';

                // If we have pageImage and bboxNorm, extract text from the new position
                if (pageImage && bboxNorm && Array.isArray(bboxNorm) && bboxNorm.length === 4) {
                    console.log('[handleAnnotationMoved] Extracting text from new position');
                    try {
                        const resp = await apiFetch(`${API_BASE}/extract-snippet-text`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({
                                page_image: pageImage,
                                bbox_norm: bboxNorm,
                            }),
                        });

                        let data = {};
                        try {
                            data = await resp.json();
                        } catch {}

                        extractedText = (data && data.text) || "";
                        console.log('[handleAnnotationMoved] Extracted text from API:', extractedText);
                    } catch (e) {
                        console.warn("extract-snippet-text failed", e);
                    }
                }

                // Update the annotation with the new bbox and text, then persist to database
                // Apply offset before saving to database (consistent with how bboxes are stored)
                const bboxForDb = displayBboxToPersistedBbox(bbox);
                await applyOverrideAndPersist({
                    executionId,
                    colIndex,
                    page,
                    bbox: bboxForDb,
                    text: extractedText,
                    annotationIds: [bestMatch.id].filter(Boolean),
                });

                // Update local UI state
                setOverrides(prev => ({
                    ...prev,
                    [executionId]: {
                        ...(prev[executionId] || {}),
                        [colIndex]: {page, bbox}
                    }
                }));

                // Update the table cell with the new extracted text
                if (extractedText) {
                    setEditedValues(prev => {
                        const base = prev[executionId] ?? Array(leafHeaders.length).fill(undefined);
                        const next = [...base];
                        next[colIndex] = extractedText;
                        return {...prev, [executionId]: next};
                    });
                }

                // Update the annotation in selectedAnnotations to reflect the new position
                const newUid = makeEntityKey(page, bbox);
                setSelectedAnnotations(prev => {
                    const updated = prev.map(ann => {
                        // Match by annotation ID (most reliable)
                        if (ann.annotationId === bestMatch.id) {
                            const updatedAnn = {
                                ...ann,
                                bbox: bbox,
                                page: page,
                                __uid: newUid  // Update the UID to reflect new position
                            };
                            console.log('[handleAnnotationMoved] Updated annotation by ID:', updatedAnn);
                            return updatedAnn;
                        }
                        // Fallback: match by old bbox
                        if (oldAnnotation && JSON.stringify(ann.bbox) === JSON.stringify(oldAnnotation.bbox)) {
                            const updatedAnn = {
                                ...ann,
                                bbox: bbox,
                                page: page,
                                __uid: newUid,
                                annotationId: bestMatch.id // Add the ID if it was missing
                            };
                            console.log('[handleAnnotationMoved] Updated annotation by bbox:', updatedAnn);
                            return updatedAnn;
                        }
                        return ann;
                    });
                    console.log('[handleAnnotationMoved] All annotations after update:', updated);
                    return updated;
                });
            } finally {
                // Clear processing state after all operations complete
                setProcessingCell(null);
            }
        }
    };

    // Single-click jiggle handler from table
    const handleTableCellClick = async ({executionId, page, bbox, fileObj}) => {
        const switchingExec = !selectedFile || executionId !== selectedExecutionId;
        if (switchingExec) {
            const ok = await openFile(executionId, (results.find(r => r?.data?.execution_id === executionId)?.data?.annotations || []), fileObj?.name || 'document.pdf');
            if (!ok) return;
            // give the viewer one tick to mount
            setTimeout(() => triggerPeek(page, bbox), 0);
        } else {
            triggerPeek(page, bbox);
        }
    };

    const handleTableCellHover = (cellData) => {
        if (!cellData) {
            setHoveredKeyExternal(null);
            return;
        }
        const {page, bbox} = cellData;
        if (page && Array.isArray(bbox) && bbox.length === 4) {
            setHoveredKeyExternal(makeEntityKey(page, bbox));
        } else {
            setHoveredKeyExternal(null);
        }
    };

// Redraw affordance handler
    // Redraw affordance handler with group detection (parity with results page)
    const handleCellRedraw = async ({executionId, page, bbox, fileObj, colIndex}) => {
        // Compute groupId for this column if it belongs to a key_value_group
        const computeGroupIdForCell = (execId, colIx) => {
            const res = results.find(r => r?.data?.execution_id === execId);
            if (!res?.data?.annotations || !toolDetails?.headerConfig) return null;
            const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
            headers.forEach((row, idx) => {
                if (idx === 0) {
                    row.unshift("Filename");
                    row.unshift("Updated");
                    row.unshift("Extracted");
                } else {
                    row.unshift("");
                    row.unshift("");
                    row.unshift("");
                }
            });
            const leaf = getLeafLabels(headers);
            const targetKey = norm(leaf[colIx]);
            for (const a of (res.data.annotations || [])) {
                if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                    const gid = makeGroupIdFromAnnotation(a);
                    for (const it of a.items) {
                        const itKey = norm((it?.field?.text) ?? (typeof it?.field === "string" ? it.field : ""));
                        if (itKey && (itKey === targetKey || itKey.includes(targetKey))) return gid;
                    }
                }
            }
            return null;
        };

        setRedrawGroupId(computeGroupIdForCell(executionId, colIndex));

        const switchingExec = !selectedFile || executionId !== selectedExecutionId;
        const kickOff = () => {
            const baseKeys = computeRemoveKeysForCell(executionId, colIndex);
            const merged = new Set(baseKeys);
            const ov = overrides?.[executionId]?.[colIndex];
            if (ov?.bbox) merged.add(makeEntityKey(ov.page ?? 1, ov.bbox));
            const removeKey = merged.size
                ? Array.from(merged)
                : (page && bbox ? makeEntityKey(page, bbox) : FORCE_DRAW);
            setRemoveKeyExternal(removeKey);
            setRemoveNonce(n => n + 1);
            setFocusPageExternal(page || 1);
            setAllowEdits(true);
            setActiveRedraw({
                executionId,
                colIndex,
                annotationIds: computeAnnotationIdsForCell(executionId, colIndex),
                removeKeys: Array.isArray(removeKey) ? removeKey : [removeKey],
            });
        };

        if (switchingExec) {
            const ok = await openFile(
                executionId,
                (results.find(r => r?.data?.execution_id === executionId)?.data?.annotations || []),
                fileObj?.name || 'document.pdf'
            );
            if (!ok) return;
            setTimeout(kickOff, 0);
        } else {
            kickOff();
        }
    };

    const handleCancel = () => {
        setEditingCell(null);
        setEditValue("");
    };

    // Export current page using the SAME Excel layout as the other export (3-row header, merges, borders)
    // Build a workbook using the SAME Excel layout as the table (3-row header, merges, borders)
    const buildExcelForResults = (exportResults, outFileName) => {
        // Helper: stable key for de-duping annotations (same intent as other export)
        const getAnnotationKey = (a) => {
            const base = `${a.type}_${a.label ?? a.entity_type ?? a.field?.text ?? ''}_${a.page ?? ''}_${a.bbox ?? ''}`;
            if (a.type === 'table_row' && Array.isArray(a.columns)) {
                const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
                return `${base}_${coords}`;
            }
            if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
            return base;
        };

        // Build a single workbook across all rows on the current page
        const wb = XLSX.utils.book_new();
        const ws_data = [];
        const merges = [];

        // 3 header rows (group / block label / column header) like ViewerContainer
        const header0 = []; // top group row
        const header1 = []; // middle label row
        const header2 = []; // bottom column row

// First, prepend the fixed leading columns (Extracted / Updated / Filename)
        const extracted = (t('historyTable.extracted') ?? '').trim() || 'Extracted';
        const updated = (t('historyTable.updated') ?? '').trim() || 'Last Update';
        const filename = (t('historyTable.filename') ?? '').trim() || 'Filename';

        header0.push(extracted, updated, filename); // <-- put text in row 0 for all three
        header1.push('', '', '');
        header2.push('', '', '');

// Each spans 3 rows
        merges.push({s: {r: 0, c: 0}, e: {r: 2, c: 0}}); // Extracted
        merges.push({s: {r: 0, c: 1}, e: {r: 2, c: 1}}); // Updated
        merges.push({s: {r: 0, c: 2}, e: {r: 2, c: 2}}); // Filename
        // We’ll append dynamic columns (built from annotations) after column index 3
        let colIndex = 3;

        // Borders for grouped sections (same behavior)
        let lastGroup = null;
        let currentGroupStart = null;
        const groupBoundaries = [];

        // Writers that know how to write each column over multiple data rows
        const valueWriters = [];
        let maxDataRows = 1;

        // === NEW: Build header strictly from headerConfig so ordering matches HistoryTable ===
        // Mirror HistoryTable's source of truth: toolDetails.headerConfig (plus the 3 fixed leading cells).
        // See HistoryTable's header/leaf handling. (This replaces the "first row's annotations" approach.)
        // Build the Excel header rows (header0/header1/header2) from headerConfig.
        if (toolDetails?.headerConfig?.length) {
            const cfg = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
            const rows = cfg.length;
            const cols = cfg[0]?.length || 0;

            for (let c = 0; c < cols; c++) {
                const top = rows > 0 ? (cfg[0][c] || '') : '';
                const mid = rows > 1 ? (cfg[1][c] || '') : '';
                const bot = rows > 2 ? (cfg[2][c] || '') : (rows > 1 ? mid : top);

                if (rows >= 3) {
                    header0.push(top);
                    header1.push(mid);
                    header2.push(bot);
                } else if (rows === 2) {
                    header0.push(top);
                    header1.push(bot);
                    header2.push('');
                } else {
                    header0.push(top);
                    header1.push('');
                    header2.push('');
                }
                colIndex += 1;
            }

            // Now compute merges using the same logic as HistoryTable's computeSpans
            // but simplified for Excel merge format
            const headerMatrix = [header0, header1, header2];
            const numRows = headerMatrix.length;
            const numCols = header0.length;
            const skip = Array.from({length: numRows}, () => Array(numCols).fill(false));

            for (let row = 0; row < numRows; row++) {
                for (let col = 3; col < numCols; col++) { // Start from 3 to skip fixed columns
                    if (skip[row][col]) continue;
                    const value = headerMatrix[row][col];
                    if (!value) continue;

                    let rowSpan = 1, colSpan = 1;

                    // Check vertical merge (rows below)
                    for (let r = row + 1; r < numRows; r++) {
                        if (!headerMatrix[r][col]) {
                            rowSpan++;
                            skip[r][col] = true;
                        } else break;
                    }

                    // Check horizontal merge (columns to the right)
                    for (let c = col + 1; c < numCols; c++) {
                        const v2 = headerMatrix[row][c];
                        const canMergeByValue = (v2 === '' || v2 === value);
                        if (!canMergeByValue) break;

                        // Check same parents (rows above must match)
                        let sameParents = true;
                        for (let pr = 0; pr < row; pr++) {
                            if (headerMatrix[pr][col] !== headerMatrix[pr][c]) {
                                sameParents = false;
                                break;
                            }
                        }
                        if (!sameParents) break;

                        colSpan++;
                        skip[row][c] = true;
                    }

                    // Add merge if needed
                    if (rowSpan > 1 || colSpan > 1) {
                        merges.push({
                            s: {r: row, c: col},
                            e: {r: row + rowSpan - 1, c: col + colSpan - 1}
                        });
                    }
                }
            }
        }

        // === NEW: Build value writers guided by leaf labels (same order the table renders) ===
        // Recreate the headers shape HistoryTable computes: prepend the 3 fixed cells to compute leaves.
        const headersForLeaf = JSON.parse(JSON.stringify(toolDetails?.headerConfig || []));
        headersForLeaf.forEach((row, idx) => {
            if (idx === 0) {
                row.unshift('Filename');
                row.unshift('Updated');
                row.unshift('Extracted');
            } else {
                row.unshift('');
                row.unshift('');
                row.unshift('');
            }
        });

        // Helpers already exist in this module: norm, keyFromAnnotation, getLeafLabels
        const leafLabels = getLeafLabels(headersForLeaf); // skips first 3 fixed internally
        const normLabel = (s) => (s ?? '').toString().trim().toLowerCase().replace(/[：:]+$/g, '');

        // Clear any previous writers from the removed code path
        valueWriters.length = 0;

        leafLabels.forEach((label, i) => {
            const target = normLabel(label);
            const myCol = 3 + i; // after Extracted, Updated, Filename

            valueWriters.push({
                startCol: myCol,
                width: 1,
                mergeVertical: true,
                // Fill only the first data row; history export produces a single row per item.
                fillRow: (rowIdx, row, annots) => {
                    if (rowIdx !== 0) return;
                    let text = '';
                    if (target) {
                        // 1) Prefer matches from key_value_group items (HistoryTable expands these to columns)
                        for (const a of (annots || [])) {
                            if (a?.type === 'key_value_group' && Array.isArray(a.items)) {
                                for (const it of a.items) {
                                    const itKey = normLabel((it?.field?.text) ?? (typeof it?.field === 'string' ? it.field : ''));
                                    if (itKey && (itKey === target || itKey.includes(target))) {
                                        text = it?.value?.text ?? '';
                                        break;
                                    }
                                }
                                if (text) break;
                            }
                        }
                        // 2) Fallback to first scalar annotation with matching label
                        if (!text) {
                            for (const a of (annots || [])) {
                                const k = keyFromAnnotation(a); // uses norm internally
                                if (!k) continue;
                                if (k === target || k.includes(target)) {
                                    if (a.type === 'key_value') text = a.value?.text ?? '';
                                    else if (a.type === 'entity' || a.type === 'summary_row') text = a.text ?? '';
                                    break;
                                }
                            }
                        }
                    }
                    row[myCol] = text ?? '';
                },
            });
        });

        // Build sheet rows: headers + data rows for EACH execution on this page
        ws_data.push(header0, header1, header2);

        const maxRowsAcrossAll = exportResults.reduce((max, r) => {
            const ann = r?.data?.annotations || [];
            // count table rows max to know how many data rows to emit per execution
            const m = ann.reduce((acc, a) => {
                if (a.type === 'table' && Array.isArray(a.rows)) return Math.max(acc, a.rows.length || 1);
                return acc;
            }, 1);
            return Math.max(max, m);
        }, 1);

        // For each execution, we emit N data rows:
        //   - Row 0 holds Extracted/Updated/Filename (and scalar fields) and row 1..N-1 leave those 3 cells blank
        exportResults.forEach((r) => {
            const annots = r?.data?.annotations || [];
            for (let i = 0; i < maxRowsAcrossAll; i++) {
                const row = new Array(colIndex).fill('');
                if (i === 0) {
                    row[0] = r.created_at ?? '';
                    row[1] = r.updated_at ?? '';
                    row[2] = r.fileName ?? '';
                }
                valueWriters.forEach(w => w.fillRow(i, row, annots));
                ws_data.push(row);
            }
        });

        // Merge the 3 fixed columns vertically across each execution’s data block (like scalar fields)
        if (exportResults.length > 0 && maxRowsAcrossAll > 1) {
            let rowPtr = 3; // first data row after headers
            exportResults.forEach(() => {
                merges.push({s: {r: rowPtr, c: 0}, e: {r: rowPtr + maxRowsAcrossAll - 1, c: 0}}); // Extracted
                merges.push({s: {r: rowPtr, c: 1}, e: {r: rowPtr + maxRowsAcrossAll - 1, c: 1}}); // Updated
                merges.push({s: {r: rowPtr, c: 2}, e: {r: rowPtr + maxRowsAcrossAll - 1, c: 2}}); // Filename
                rowPtr += maxRowsAcrossAll;
            });
        }

        // Create sheet and apply merges/styles (same style choices as the other exporter)
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        ws['!merges'] = merges;

        // Column widths
        ws['!cols'] = ws_data[0].map((_, colIdx) => {
            const colTexts = ws_data.map(row => row[colIdx] ?? '');
            const maxLen = Math.max(...colTexts.map(val => val.toString().length));
            return {wch: Math.min(Math.max(10, maxLen + 2), 60)};
        });
        // Header style (centered, bold, grey, boxed) — matches the other export
        const centerBoldHeader = {
            alignment: {horizontal: 'center', vertical: 'center'},
            font: {bold: true},
            border: {
                top: {style: 'thin', color: {rgb: '000000'}},
                bottom: {style: 'thin', color: {rgb: '000000'}},
                left: {style: 'thin', color: {rgb: '000000'}},
                right: {style: 'thin', color: {rgb: '000000'}},
            },
            fill: {fgColor: {rgb: 'E0E0E0'}}
        };
        for (let c = 0; c < colIndex; c++) {
            [0, 1, 2].forEach(r => {
                const cell = XLSX.utils.encode_cell({r, c});
                if (ws[cell]) ws[cell].s = centerBoldHeader;
            });
        }

// Apply borders: solid vertical separators for metadata cols (0,1,2),
// hairline inside everything else; keep existing group edges.
        for (let r = 3; r < ws_data.length; r++) {
            for (let c = 0; c < colIndex; c++) {
                const cell = XLSX.utils.encode_cell({r, c});

                const isMetadataCol = (c === 0 || c === 1 || c === 2);
                const isStartOfData = (c === 3);                // left wall of dynamic data
                const isEndOfSheet = (c === colIndex - 1);     // right wall

                const border = {
                    top: {style: 'hair', color: {rgb: '999999'}},
                    bottom: {style: 'hair', color: {rgb: '999999'}},
                    left: {style: 'none'},
                    right: {style: 'none'}
                };

                if (isMetadataCol) {
                    // solid separators between Extracted | Updated | Filename and to the right of Filename
                    if (c === 0) border.left = {style: 'thin', color: {rgb: '000000'}};
                    border.right = {style: 'thin', color: {rgb: '000000'}};
                } else {
                    // keep existing group “walls”
                    if (isStartOfData) border.left = {style: 'thin', color: {rgb: '000000'}};
                    if (isEndOfSheet) border.right = {style: 'thin', color: {rgb: '000000'}};
                }

                const style = {alignment: {horizontal: 'left', vertical: 'center'}, border};
                if (ws[cell]) ws[cell].s = style;
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'History');
        XLSX.writeFile(wb, outFileName);
    };

    // Export actions
    const exportCurrentPageToExcel = () => {
        buildExcelForResults(results, `history_page_${page}.xlsx`);
    };

    const exportSelectedToExcel = () => {
        const selectedResults = results.filter(r => selectedIds.has(r?.data?.execution_id));
        if (selectedResults.length > 0) {
            buildExcelForResults(selectedResults, `history_selected.xlsx`);
        } else {
            alert(t('nothing_to_export') || 'Nothing to export.');
        }
    };

    const exportAllToExcel = async () => {
        // Fetch every page and export one combined workbook
        const all = [];
        let p = 1;
        let hasNext = true;
        while (hasNext) {
            const url = `${API_BASE}/list_executions?tool_id=${toolId}&page=${p}&page_size=${pageSize}`;
            const res = await apiFetch(url);
            if (!res.ok) break;
            const data = await res.json();
            const pageResults = (data.items || []).map(ex => ({
                fileName: ex.file_name,
                status: 'success',
                data: {execution_id: ex.id, annotations: ex.result_json},
                created_at: ex.created_at,
                updated_at: ex.updated_at
            }));
            all.push(...pageResults);
            hasNext = !!data.has_next;
            p += 1;
        }
        if (all.length > 0) {
            buildExcelForResults(all, `history_all.xlsx`);
        } else {
            alert(t('nothing_to_export') || 'Nothing to export.');
        }
    };

    // Export menu state
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const openExportMenu = (e) => setExportMenuAnchor(e.currentTarget);
    const closeExportMenu = () => setExportMenuAnchor(null);


    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            sx={{height: '100dvh', overflow: 'hidden'}} // Prevent body scroll
        >
            {/* === MODIFICATION 1: REPLACED HEADER === */}
            {/* The old fixed/absolute elements have been replaced with this flexbox container */}
            <Box
                display="flex"
                alignItems="center"
                pt={4} pl={2} pr={3} // Moved padding from root
                justifyContent="space-between"
                width="100%"
                mb={{xs: 3, sm: 4}}
            >
                <Box>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/home')}
                    >
                        {t('homepage')}
                    </Button>
                </Box>
                <Box sx={{flex: 1, minWidth: 0, mx: 2, textAlign: 'center'}}>
                    <Typography
                        variant="h4"
                        noWrap
                        title={`${t('history')} — ${toolName}`}
                    >
                        {t('history')} — {toolName}
                    </Typography>
                </Box>
                <Box>
                    <LanguageThemeBar/>
                </Box>
            </Box>

            <Box
                display="flex"
                width="100%"
                flex="1 1 auto"
                alignItems="stretch"
                sx={{minHeight: 0, pl: 2, pr: 3, pb: 4}} // Moved padding from root
            >
                <Box
                    // === MODIFICATION 2: ADDED MARGIN FOR THE VIEWER ===
                    width={selectedFile ? `calc(100% - ${viewerWidthPct}% - 16px)` : '100%'}
                    height="100%"
                >
                    <HistoryTable
                        loading={loading}
                        results={results}
                        toolDetails={toolDetails}
                        editedValues={editedValues}
                        editingCell={editingCell}
                        setEditingCell={setEditingCell}
                        editValue={editValue}
                        setEditValue={setEditValue}
                        handleConfirm={handleConfirm}
                        handleCancel={handleCancel}
                        theme={theme}
                        onFilenameClick={(executionId, annots, fileName) => openFile(executionId, annots, fileName)}
                        page={page}
                        hasNext={paged.has_next}
                        hasPrev={paged.has_prev}
                        onNext={() => loadPage(page + 1)}
                        onPrev={() => loadPage(page - 1)}
                        total={paged.total}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            loadPage(1);
                        }}
                        selectedFile={selectedFile}
                        selectedExecutionId={selectedExecutionId}
                        // 🔁 click = jiggle, dbl-click = edit
                        onCellClick={async ({executionId, page, bbox}) => {
                            if (!selectedFile || executionId !== selectedExecutionId) {
                                const res = results.find(r => r?.data?.execution_id === executionId);
                                if (res) {
                                    const ok = await openFile(executionId, res.data.annotations, res.fileName);
                                    if (ok) setTimeout(() => triggerPeek(page, bbox), 0);
                                    return;
                                }
                            }
                            triggerPeek(page, bbox);
                        }}
                        onCellDoubleClick={({executionId, fileName, index, value}) => {
                            setEditingCell({executionId, fileName, index});
                            setEditValue(value ?? "");
                        }}
                        onCellHover={handleTableCellHover}
                        hoveredKeyExternal={hoveredKeyExternal}
                        onCellRedraw={handleCellRedraw}
                        overrides={overrides}
                        onHeaderEdit={handleHeaderEdit}
                        extractingCell={extractingCell}
                        processingCell={processingCell}
                        leftExtra={bulkActions}
                        rightExtra={
                            <>
                                <Button
                                    size="small"
                                    variant="contained"
                                    endIcon={<ArrowDropDownRoundedIcon/>}
                                    onClick={openExportMenu}
                                    disabled={!toolDetails?.headerConfig || !results?.length}
                                >
                                    {t('export_to_excel')}
                                </Button>
                                <Menu
                                    anchorEl={exportMenuAnchor}
                                    open={Boolean(exportMenuAnchor)}
                                    onClose={closeExportMenu}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            closeExportMenu();
                                            exportCurrentPageToExcel();
                                        }}
                                    >
                                        <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                                        <ListItemText primary={t('export_current_page') || 'Export current page'}/>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={async () => {
                                            closeExportMenu();
                                            await exportAllToExcel();
                                        }}
                                    >
                                        <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                                        <ListItemText primary={t('export_all') || 'Export all'}/>
                                    </MenuItem>
                                    <MenuItem
                                        disabled={selectedIds.size === 0}
                                        onClick={() => {
                                            closeExportMenu();
                                            exportSelectedToExcel();
                                        }}
                                    >
                                        <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                                        <ListItemText primary={t('export_selected_rows') || 'Export selected rows'}/>
                                    </MenuItem>
                                </Menu>
                            </>
                        }
                        onToggleRow={toggleRowSelected}
                        selectedIds={selectedIds}
                    />
                </Box>

                {selectedFile && (
                    <Box sx={{
                        position: 'fixed',
                        top: 100,
                        right: 10,
                        bottom: 0,
                        width: `${viewerWidthPct}%`,
                        bgcolor: 'background.paper',
                        zIndex: 1200
                    }}>

                        <Fab
                            variant="extended"
                            color="primary"
                            size="medium"
                            onClick={closeViewer}
                            aria-label="Close viewer"
                            sx={(theme) => ({
                                position: 'absolute',
                                zIndex: 2500,
                                top: `max(${theme.spacing(1)}, env(safe-area-inset-top))`,
                                right: `max(${theme.spacing(1)}, env(safe-area-inset-right))`,
                                textTransform: 'none',
                                height: 30,
                                borderRadius: 999,
                                px: 1.75,
                                boxShadow: 'none',
                                backgroundColor: alpha(theme.palette.primary.main, 0.8), // 👈 80% opaque
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.9), // slightly less transparent on hover
                                    boxShadow: 'none',
                                },
                            })}
                        >
                            <CloseRoundedIcon sx={{mr: 1}}/>
                            {t("close")}
                        </Fab>

                        <VerticalResizeHandle
                            ariaLabel="Resize table/viewer"
                            rightCss={`calc(${viewerWidthPct}% - 3px)`}   // centers the 6px handle on the edge
                            onDrag={(dx) => {
                                // Positive dx => moving right; the overlay’s left edge moves right => viewer gets narrower.
                                // Convert dx in pixels to % of the window:
                                const winW = window.innerWidth || 1;
                                const deltaPct = (dx / winW) * 100;
                                setViewerWidthPct((p) => clamp(p - deltaPct, 35, 65)); // keep 35–65% bounds
                            }}
                        />
                        <ViewerContainer
                            key={`${selectedExecutionId}_${viewerKey}`}  // Force remount when switching documents or annotations change
                            showHeader={false}
                            selectedAnnotations={selectedAnnotations}
                            setSelectedAnnotations={setSelectedAnnotations}
                            defaultGroupIdForNewAnns={redrawGroupId}
                            groupMeta={{}}
                            setGroupMeta={() => {
                            }}
                            file_input={selectedFile}        // <<< same as ToolResultsPage
                            hideSelectionPanel
                            readOnlyHighlights={false}
                            forceAllSelected
                            hideToolbar={false}
                            showZoomControls
                            showAnnotateControls={false}
                            preventAutoExtract
                            containerWidth="100%"
                            paperSx={{maxWidth: 'none'}}
                            // 👇 feed the peek key + nonce so repeated clicks always jiggle
                            peekKeyExternal={peekKeyExternal}
                            peekNonceExternal={peekNonce}
                            hoveredKeyExternal={hoveredKeyExternal}
                            onBoxHover={setHoveredKeyExternal}
                            removeKeyExternal={removeKeyExternal}
                            removeNonceExternal={removeNonce}
                            focusPageExternal={focusPageExternal}
                            onAnnotationAdded={handleAnnotationAdded}
                            onAnnotationMoved={handleAnnotationMoved}
                            disableReprocessOnDrag={true}
                        />
                    </Box>
                )}
            </Box>

            {/* Delete confirmation dialog */}
            <ConfirmDeleteDialog
                open={deleteOpen}
                count={selectedIds.size}
                loading={deleting}
                onCancel={() => !deleting && setDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </Box>
    );
}
